/**
 * Materials Page
 *
 * Materiais de apoio para o usuário. O botão "Visualizar" abre o documento
 * (o navegador renderiza o PDF/imagem/link em uma nova aba), de onde o usuário
 * pode baixar caso queira.
 */
import { useState } from 'react';
import {
  Search,
  X,
  FileText,
  ExternalLink,
  Image as ImageIcon,
  PlayCircle,
  File,
  Eye,
  Loader2,
  FolderOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useMaterials } from '@/hooks/use-learning';
import { resolveMediaUrl } from '@/lib/api';
import { MATERIAL_TYPE_LABELS } from '@/types/learning';

const materialIcons: Record<string, React.ElementType> = {
  pdf: FileText,
  link: ExternalLink,
  image: ImageIcon,
  video: PlayCircle,
  document: File,
};

export default function MaterialsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  
  const { data, isLoading } = useMaterials(searchTerm, page);
  
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };
  
  const handleView = (url: string) => {
    // Abre o documento em nova aba para visualização; o usuário pode baixar
    // pelo próprio visualizador do navegador caso queira. Resolve URLs de
    // arquivos servidos pelo backend (/uploads) para a origem correta.
    window.open(resolveMediaUrl(url), '_blank', 'noopener,noreferrer');
  };
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FolderOpen className="h-6 w-6 text-primary" />
            Materiais de Apoio
          </h1>
          <p className="text-muted-foreground mt-1">
            Visualize materiais complementares para seu aprendizado
          </p>
        </div>
      </div>
      
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar materiais..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchTerm.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={() => handleSearch('')}
                aria-label="Limpar busca de materiais"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Materials List */}
      {(() => {
        if (isLoading) return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (!data || data.materials.length === 0) return (
            <Card>
              <CardContent className="py-12 text-center">
                <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum material encontrado</h3>
                <p className="text-muted-foreground">
                  {searchTerm
                    ? 'Tente ajustar sua busca.'
                    : 'Ainda não há materiais de apoio disponíveis.'}
                </p>
              </CardContent>
            </Card>
        );
        return (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {data.materials.map((material) => {
                const IconComponent = materialIcons[material.type] || File;
            
                return (
                  <Card key={material.id} className="flex flex-col h-full hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <IconComponent className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            {/* Sobrescreve o auto-encolhimento global do CardTitle para que
                                todos os títulos de material fiquem no mesmo tamanho (12px),
                                quebrando linha quando necessário em vez de reduzir a fonte. */}
                            <CardTitle
                              className="text-xs"
                              style={{
                                fontSize: '12px',
                                whiteSpace: 'normal',
                                overflow: 'visible',
                                textOverflow: 'clip',
                              }}
                            >
                              {material.title}
                            </CardTitle>
                            {material.lesson_title && (
                              <CardDescription className="text-xs">
                                {material.lesson_title}
                              </CardDescription>
                            )}
                          </div>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {MATERIAL_TYPE_LABELS[material.type] || material.type}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-2 flex flex-1 flex-col">
                      {material.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {material.description}
                        </p>
                      )}
                      <div className="mt-auto flex items-center justify-between">
                        {material.size && (
                          <span className="text-xs text-muted-foreground">{material.size}</span>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleView(material.url)}
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Visualizar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
        );
      })()}
      
      {/* Pagination */}
      {data && data.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Página {page} de {data.total_pages}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.min(data.total_pages, p + 1))}
            disabled={page === data.total_pages}
          >
            Próxima
          </Button>
        </div>
      )}
    </div>
  );
}
